import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Copy, Mail, Link as LinkIcon, QrCode as QrCodeIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

interface ShareProposalDialogProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  estimateNumber: string;
  customerEmail?: string;
}

export function ShareProposalDialog({ 
  open, 
  onClose, 
  estimateId, 
  estimateNumber,
  customerEmail 
}: ShareProposalDialogProps) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [expirationDays, setExpirationDays] = useState("30");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const publicUrl = accessToken 
    ? `${window.location.origin}/proposal/public/${estimateId}/${accessToken}`
    : null;

  useEffect(() => {
    if (open) {
      loadExistingToken();
    }
  }, [open, estimateId]);

  const loadExistingToken = async () => {
    const { data } = await supabase
      .from("estimates")
      .select("public_access_token, public_access_enabled")
      .eq("id", estimateId)
      .single();

    if (data?.public_access_enabled && data?.public_access_token) {
      setAccessToken(data.public_access_token);
    }
  };

  const generateToken = async () => {
    try {
      setIsGenerating(true);
      const token = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(expirationDays));

      const { error } = await supabase
        .from("estimates")
        .update({
          public_access_token: token,
          public_access_enabled: true,
          public_access_expires_at: expiresAt.toISOString()
        })
        .eq("id", estimateId);

      if (error) throw error;

      setAccessToken(token);
      toast.success("Public link generated!");
    } catch (error) {
      toast.error("Failed to generate link");
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  const disableLink = async () => {
    try {
      const { error } = await supabase
        .from("estimates")
        .update({ public_access_enabled: false })
        .eq("id", estimateId);

      if (error) throw error;

      toast.success("Link disabled");
      onClose();
    } catch (error) {
      toast.error("Failed to disable link");
      console.error(error);
    }
  };

  const sendEmail = async () => {
    if (!customerEmail) {
      toast.error("No customer email address available");
      return;
    }

    try {
      setIsSendingEmail(true);
      
      const { error } = await supabase.functions.invoke("send-proposal-link", {
        body: {
          estimateId,
          estimateNumber,
          customerEmail,
          publicUrl
        }
      });

      if (error) throw error;

      toast.success("Email sent successfully!");
    } catch (error) {
      toast.error("Failed to send email");
      console.error(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Proposal with Customer</DialogTitle>
          <DialogDescription>
            Generate a secure link that allows your customer to view, accept, and sign the proposal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {!accessToken ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Link Expiration</Label>
                <Select value={expirationDays} onValueChange={setExpirationDays}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={generateToken} disabled={isGenerating} className="w-full">
                <LinkIcon className="mr-2 h-4 w-4" />
                {isGenerating ? "Generating..." : "Generate Public Link"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Public Link</Label>
                <div className="flex gap-2">
                  <Input value={publicUrl || ""} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copyToClipboard}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  {customerEmail && (
                    <Button variant="outline" size="icon" onClick={sendEmail} disabled={isSendingEmail}>
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with your customer via email, SMS, or any other method.
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowQR(!showQR)} className="flex-1">
                  <QrCodeIcon className="mr-2 h-4 w-4" />
                  {showQR ? "Hide" : "Show"} QR Code
                </Button>
              </div>

              {showQR && publicUrl && (
                <div className="flex justify-center p-6 bg-background border rounded-lg">
                  <QRCodeSVG value={publicUrl} size={200} level="H" />
                </div>
              )}

              <div className="bg-muted/30 rounded-lg p-4 border">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="font-medium text-primary">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expires:</span>
                    <span className="font-medium">{expirationDays} days</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {accessToken && (
            <Button variant="destructive" onClick={disableLink}>
              Disable Link
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
